import { Column } from "@ant-design/charts";
import { Col, Row } from "antd";
import dayjs from "dayjs";
import { useZsplFleetStatus, useZsplIrregularDrives } from "misc/api/datalayer";
import React, { useMemo } from "react";
import { useHistory } from "react-router";

import { Card } from "misc/card";
import { NumberField, TextField } from "misc/fields";
import { stsColors } from "misc/styles/Colors";
import { Table } from "misc/tables";
import { clientUrls } from "misc/urls";

import SideHeader from "navigation/SideHeader";
import { useTranslation } from "react-i18next";

const ZsplDashboard = () => {
    const [t] = useTranslation();
    const history = useHistory();
    const today = dayjs().format("YYYY-MM-DD");
    const { fleetStatus, isLoading, isRefetching } = useZsplFleetStatus(today);
    const {
        irregularDrives,
        isLoading: isLoadingIrregularDrives,
        isRefetching: isRefetchingIrregularDrives,
        isError: isErrorIrregularDrives
    } = useZsplIrregularDrives(dayjs().subtract(10, "weeks").startOf("week").format("YYYY-MM-DD"), dayjs().startOf("week").format("YYYY-MM-DD"));

    const irregularDrivesChartData = useMemo(() => {
        const labelKeys = {
            10: "t_weeks_ago_10",
            5: "t_weeks_ago_5",
            2: "t_weeks_ago_2",
            1: "t_weeks_ago_1"
        };

        return [10, 5, 2, 1].map(weeksAgo => {
            const targetWeek = dayjs().startOf("week").subtract(weeksAgo, "weeks").format("YYYY-MM-DD");

            return {
                periodLabel: t(labelKeys[weeksAgo]),
                value: irregularDrives.reduce((total, depot) => {
                    const week = (depot.weeks || []).find(item => item.week === targetWeek);

                    return total + (Number(week?.value) || 0);
                }, 0)
            };
        });
    }, [irregularDrives, t]);

    const locationColumns = [
        {
            title: <TextField value={t("t_organisational_unit_depot")} />,
            dataIndex: "name",
            key: "name",
            render: value => <TextField value={value} style={{ fontWeight: 600 }} />,
            width: 220
        },
        {
            title: <TextField value={t("t_total_vehicles")} />,
            dataIndex: "total_vehicles",
            key: "total_vehicles",
            render: value => (value ? <NumberField value={value} /> : <TextField />),
            width: 140
        },
        {
            title: <TextField value={t("t_available")} />,
            dataIndex: "available",
            key: "available",
            render: value => (value ? <NumberField value={value} /> : <TextField />),
            width: 130
        },
        {
            title: <TextField value={t("t_not_available")} />,
            dataIndex: "not_available",
            key: "not_available",
            render: value => (value ? <NumberField value={value} /> : <TextField />),
            width: 160
        },
        {
            title: <TextField value={t("t_to_be_checked")} />,
            dataIndex: "to_be_checked",
            key: "to_be_checked",
            render: value => (value ? <NumberField value={value} /> : <TextField />),
            width: 140
        },
        {
            title: <TextField value={t("t_low_battery")} />,
            dataIndex: "low_battery",
            key: "low_battery",
            render: value => (value ? <NumberField value={value} /> : <TextField />),
            width: 190
        }
    ];

    const irregularDrivesColumnConfig = {
        yField: "value",
        legend: false,
        columnStyle: {
            radius: [0, 0, 0, 0]
        },
        xField: "periodLabel",
        seriesField: "periodLabel",
        color: [stsColors.lightblue, stsColors.lightblue, stsColors.lightblue, stsColors.blue],
        tooltip: {
            title: "periodLabel",
            formatter: ({ value }) => ({
                name: t("t_irregular_drives"),
                value
            })
        }
    };

    const behaviorColumnConfig = {
        ...irregularDrivesColumnConfig,
        tooltip: {
            title: "periodLabel",
            formatter: ({ value }) => ({
                name: t("t_zspl_reverse_driving"),
                value
            })
        }
    };

    return (
        <SideHeader>
            <Card title="t_dashboard" stretchHeight>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Table
                            dataSource={fleetStatus}
                            columns={locationColumns}
                            rowKey="depot_id"
                            isLoading={isLoading}
                            isUpdating={isRefetching}
                            onRow={record => ({
                                onClick: () => history.push(clientUrls.zspl.depot(record.depot_id), { depotName: record.name, date: today }),
                                style: { cursor: "pointer" }
                            })}
                            pagination={false}
                            size="small"
                            scroll={{ x: "100%" }}
                            sticky
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Card
                            title="t_irregular_drives"
                            height={300}
                            isLoading={isLoadingIrregularDrives}
                            isUpdating={isRefetchingIrregularDrives}
                            isError={isErrorIrregularDrives}
                        >
                            <Column {...irregularDrivesColumnConfig} data={irregularDrivesChartData} height={220} />
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card title="t_zspl_reverse_driving" height={300}>
                            {/* Mock only until the reverse-driving API is available. */}
                            <Column {...behaviorColumnConfig} data={irregularDrivesChartData} height={220} />
                        </Card>
                    </Col>
                    <Col span={24}>
                        <Card title="Energieverbrauch">
                            <TextField value="Energie chart" />
                        </Card>
                    </Col>
                </Row>
            </Card>
        </SideHeader>
    );
};

export default ZsplDashboard;
